import { Box, Typography, Grid, Button, Snackbar, Alert } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import { useState } from 'react';
import { SectionCard, StoplightChip } from '../../components/common';
import { MealLoggingModal, TodayTasksWidget, WeeklySummaryCards } from '../../components/patient';
import patientHomeData from '../../mocks/patientHome.json';
import type { LoggedMealRecord } from '../../components/patient/MealLoggingModal';

export const PatientHomePage = () => {
  const { patient, insights, recentMeals } = patientHomeData;
  const [mealLoggingOpen, setMealLoggingOpen] = useState(false);
  const [showMealSaved, setShowMealSaved] = useState(false);
  const [recentMealsState, setRecentMealsState] = useState(recentMeals);

  const handleMealSave = (meal: LoggedMealRecord) => {
    setRecentMealsState((prev) => [meal, ...prev]);
    setShowMealSaved(true);
  };

  // Adapt insights for WeeklySummaryCards
  const insightCards = insights.map(insight => ({
    id: insight.id,
    cardType: insight.insightType,
    title: insight.title,
    summary: insight.summary,
    confidence: 0.85,
    status: (insight.viewedByPatient ? "VIEWED" : "NEW") as "NEW" | "VIEWED" | "RESOLVED",
    createdAt: insight.createdAt
  }));

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Welcome back, {patient.firstName}! 👋
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Here's your personalized nutrition dashboard
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TodayTasksWidget />
        </Grid>

        {/* Recent Meals */}
        <Grid item xs={12} md={6}>
          <SectionCard
            title="Recent Meals"
            subtitle="Last 24 hours"
            actions={
              <Button
                size="small"
                onClick={() => setMealLoggingOpen(true)}
                startIcon={<RestaurantIcon />}
              >
                Log Meal
              </Button>
            }
          >
            {recentMealsState && recentMealsState.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {recentMealsState.slice(0, 3).map((meal) => (
                  <Box key={meal.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {meal.mealType}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {meal.description}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {meal.items && meal.items.slice(0, 2).map((item, idx) => (
                        <StoplightChip
                          key={idx}
                          classification={item.stoplightClassification as "GREEN" | "YELLOW" | "RED"}
                          size="small"
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No meals logged yet. Start tracking your meals to see insights!
              </Typography>
            )}
          </SectionCard>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={6}>
          <SectionCard title="Quick Actions">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                fullWidth
                onClick={() => setMealLoggingOpen(true)}
                startIcon={<RestaurantIcon />}
              >
                Log a Meal
              </Button>
              <Button
                variant="outlined"
                fullWidth
                component={RouterLink}
                to="/patient/check-in"
              >
                Open Check-in Form
              </Button>
              <Button
                variant="outlined"
                fullWidth
                component={RouterLink}
                to="/patient/progress"
                startIcon={<TrendingUpIcon />}
              >
                View Progress
              </Button>
            </Box>
          </SectionCard>
        </Grid>

        {/* Weekly Insights */}
        <Grid item xs={12}>
          <Typography variant="h5" gutterBottom sx={{ mt: 2 }}>
            Your Insights
          </Typography>
          <WeeklySummaryCards insights={insightCards} />
        </Grid>
      </Grid>

      <MealLoggingModal
        open={mealLoggingOpen}
        onClose={() => setMealLoggingOpen(false)}
        onSave={handleMealSave}
      />

      <Snackbar
        open={showMealSaved}
        autoHideDuration={3000}
        onClose={() => setShowMealSaved(false)}
      >
        <Alert severity="success" onClose={() => setShowMealSaved(false)}>
          Meal logged successfully.
        </Alert>
      </Snackbar>

    </Box>
  );
};
